export interface WellnessCheckAdditionalCategory {
	name: string;
	tooltip: string;
	options: string[];
}

export interface WellnessCheckGroup {
	categories: WellnessCheckAdditionalCategory[];
}

/** API returns one or more named groups; each group has its own categories. */
export type WellnessCheckAdditionalCategoriesResponse =
	| Record<string, WellnessCheckGroup>
	| Array<Record<string, WellnessCheckGroup>>;

/** Synthetic category: textbox only, no predefined options. Unmatched/other content. Stored as topicId in subjective. */
export const WELLNESS_CHECK_OTHER_ID = "Other";

/** Safe fragment for DOM id/selector: "::" is invalid in CSS selectors, use "--". */
export function wellnessCheckTopicIdToSafeId(topicId: string): string {
	return topicId.replace(/::/g, "--");
}

/** topicId format when multiple groups: "groupName::categoryName". Single group uses plain category name. */
export function getWellnessCheckTopicId(
	groupName: string,
	categoryName: string,
	groupCount: number
): string {
	return groupCount > 1 ? `${groupName}::${categoryName}` : categoryName;
}

export interface WellnessCheckGroupEntry {
	groupName: string;
	categories: WellnessCheckAdditionalCategory[];
}

/** Normalize API response (object or array of single-key objects) to a flat list of group entries, sorted by groupName. */
export function normalizeWellnessCheckGroups(
	data: WellnessCheckAdditionalCategoriesResponse | null | undefined
): WellnessCheckGroupEntry[] {
	if (!data) return [];
	const entries: WellnessCheckGroupEntry[] = Array.isArray(data)
		? data.flatMap((item) =>
				Object.entries(item).map(([groupName, group]) => ({
					groupName,
					categories: group?.categories ?? [],
				}))
			)
		: Object.entries(data).map(([groupName, group]) => ({
				groupName,
				categories: group?.categories ?? [],
			}));
	return entries.sort((a, b) => a.groupName.localeCompare(b.groupName));
}

/** Format group name for tab/label display: WELLNESS_CHECK_2 -> "Wellness Check 2" */
export function formatWellnessCheckGroupLabel(groupName: string): string {
	return groupName
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join(" ");
}

/** True when type is a wellness-check group (not COACHING, not OTHER). */
export function isWellnessType(type: string): boolean {
	return type !== "COACHING" && type !== "OTHER";
}
